import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  ScreenLayout,
  Modal,
  Button,
  Input,
  Avatar,
  StatusBadge,
  IconRabbit,
  IconCheck,
  usePref,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  ADOPTION_STATUSES,
  CHARACTER_OPTIONS,
  adoptionStatusOf,
  assetToCat,
  seedCats,
  type AdoptionStatus,
  type AppTab,
  type Cat,
} from './data.js';
import {
  CATS_STATS_PREF_KEY,
  CAT_STAT_DEFS,
  DEFAULT_CAT_STATS,
  DEFAULT_FILTERS,
  FILTERS_PREF_KEY,
  STATS_MAX,
  catFilterPrefId,
  type CatStatId,
} from './settings-defs.js';

/**
 * Ecran liste des chats du bar (zone chats de la bascule).
 *
 * Regles de la cage : donnees via `sdk.assets.list()` (filtre type 'cat') et
 * `sdk.assets.create()` pour ajouter un chat a la main, UI 100% @taki/ui, zero
 * hex. ScreenLayout a la racine avec chargement / erreur branches ; l'etat vide
 * (aucun chat en base) est gere dans children pour porter le bouton de seed.
 *
 * Reglages : ils vivent dans settings.tsx (hub Parametres du shell) ; l'ecran
 * LIT les prefs partagees ("app.cat-manager-six.filters" et
 * "app.cat-manager-six.stats.cats") via `usePref` et se resynchronise en direct.
 */
export interface CatsListScreenProps {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  onSelectCat: (catId: string) => void;
  /** Demande d'ouverture du Modal d'ajout (action du dock relayee par la racine). */
  openFormRequested: boolean;
  /** Acquitte la demande une fois le Modal ouvert (la racine la remet a zero). */
  onOpenFormHandled: () => void;
}

export function CatsListScreen({
  activeTab,
  onTabChange,
  onSelectCat,
  openFormRequested,
  onOpenFormHandled,
}: CatsListScreenProps): JSX.Element {
  const { sdk, config } = useTakiApp();

  const [cats, setCats] = useState<Cat[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [seeding, setSeeding] = useState(false);

  // Prefs partagees avec settings.tsx (hub Parametres du shell) : visibilite
  // des pastilles de statut (zone 4) et stats d'en-tete affichees (zone 3).
  const [filterVisibility] = usePref<Record<string, boolean>>(
    FILTERS_PREF_KEY,
    DEFAULT_FILTERS,
  );
  const [statIds] = usePref<string[]>(CATS_STATS_PREF_KEY, DEFAULT_CAT_STATS);

  // Formulaire d'ajout d'un chat.
  const [showForm, setShowForm] = useState(false);
  const [fName, setFName] = useState('');
  const [fStatus, setFStatus] = useState<AdoptionStatus>('resident');
  const [fCharacter, setFCharacter] = useState<string[]>([]);
  const [fBirthYear, setFBirthYear] = useState('');
  const [fDiet, setFDiet] = useState('');
  const [fPhoto, setFPhoto] = useState('');
  const [fVaccines, setFVaccines] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const list = await sdk.assets.list();
      setCats(list.filter((asset) => asset.type === 'cat').map(assetToCat));
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Echec du chargement.');
    } finally {
      setLoadingList(false);
    }
  }, [sdk]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Action du dock relayee par l'ecran racine : ouvrir le Modal d'ajout puis
  // acquitter (la demande repasse a false, l'effet ne rejoue pas).
  useEffect(() => {
    if (openFormRequested) {
      setShowForm(true);
      onOpenFormHandled();
    }
  }, [openFormRequested, onOpenFormHandled]);

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    setListError(null);
    try {
      await seedCats(sdk, config.orgId);
      await refresh();
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Echec de la creation des chats.');
    } finally {
      setSeeding(false);
    }
  }, [sdk, config.orgId, refresh]);

  const resetForm = useCallback(() => {
    setFName('');
    setFStatus('resident');
    setFCharacter([]);
    setFBirthYear('');
    setFDiet('');
    setFPhoto('');
    setFVaccines(true);
    setSaveError(null);
  }, []);

  /** Ferme le modal d'ajout et remet le formulaire a zero. */
  const closeForm = useCallback(() => {
    setShowForm(false);
    resetForm();
  }, [resetForm]);

  const toggleCharacter = useCallback((value: string) => {
    setFCharacter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }, []);

  const handleCreate = useCallback(async () => {
    const name = fName.trim();
    if (name === '') {
      setSaveError('Le nom du chat est obligatoire.');
      return;
    }
    const yearStr = fBirthYear.trim();
    const year = yearStr === '' ? null : Number.parseInt(yearStr, 10);
    if (year !== null && Number.isNaN(year)) {
      setSaveError('Annee de naissance invalide.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await sdk.assets.create({
        orgId: config.orgId,
        type: 'cat',
        name,
        customFields: {
          character: fCharacter,
          adoptionStatus: fStatus,
          photoUrl: fPhoto.trim(),
          birthYear: year,
          diet: fDiet.trim(),
          vaccinesUpToDate: fVaccines,
        },
      });
      closeForm();
      await refresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Echec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  }, [sdk, config.orgId, fName, fStatus, fCharacter, fBirthYear, fDiet, fPhoto, fVaccines, closeForm, refresh]);

  const filteredCats = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return cats.filter((cat) => {
      const matchesQuery = needle === '' || cat.name.toLowerCase().includes(needle);
      const matchesStatus = statusFilter === 'all' || cat.adoptionStatus === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [cats, query, statusFilter]);

  // Pas d'icone dans le menu : l'identite visuelle de l'app vit dans la puce
  // d'icone en tete du rail, jamais repetee (regle G2).
  const menuItems = [
    { id: 'cats', label: 'Les chats' },
    { id: 'visits', label: 'Les visites' },
  ];

  // Pastilles de statut : seules celles laissees visibles dans les reglages
  // (pref, cle absente = visible) s'affichent ; 'Tous' reste toujours la
  // (c'est le repli).
  const filters = useMemo(
    () => [
      { id: 'all', label: 'Tous' },
      ...ADOPTION_STATUSES.filter(
        (status) => filterVisibility[catFilterPrefId(status.value)] !== false,
      ).map((status) => ({ id: status.value, label: status.label })),
    ],
    [filterVisibility],
  );

  // Si la pastille ACTIVE vient d'etre masquee dans les reglages, le filtre
  // retombe sur 'Tous' (jamais de filtre actif invisible).
  useEffect(() => {
    if (statusFilter !== 'all' && filterVisibility[catFilterPrefId(statusFilter)] === false) {
      setStatusFilter('all');
    }
  }, [filterVisibility, statusFilter]);

  // Valeurs de TOUTES les stats du catalogue, calculees des donnees reelles.
  // La pref ne choisit que LESQUELLES afficher, jamais les valeurs.
  const statValues = useMemo<Record<CatStatId, string>>(() => {
    const adoptable = cats.filter((cat) => cat.adoptionStatus === 'adoptable').length;
    const adopte = cats.filter((cat) => cat.adoptionStatus === 'adopte').length;
    const vaccines = cats.filter((cat) => cat.vaccinesUpToDate).length;
    return {
      total: String(cats.length),
      adoptable: String(adoptable),
      adopte: String(adopte),
      vaccines: String(vaccines),
    };
  }, [cats]);

  // Stats de la zone 3 : la selection de la pref, dans l'ordre du catalogue,
  // plafonnee a STATS_MAX (garde-fou si la pref stockee depasse le contrat).
  const headerStats = useMemo(
    () =>
      CAT_STAT_DEFS.filter((def) => statIds.includes(def.id))
        .slice(0, STATS_MAX)
        .map((def) => ({ value: statValues[def.id], label: def.label })),
    [statIds, statValues],
  );

  let content: JSX.Element | null = null;
  if (cats.length === 0) {
    // Aucun chat en base : etat vide gere ici (children) pour porter le bouton
    // de seed. A plat, sans Card (regle G9) ; il ne depend pas du modal
    // d'ajout, qui flotte au-dessus (regle G6).
    content = (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <p className="text-[0.9375rem] font-semibold text-ink-900">Aucun chat</p>
        <p className="text-[0.8125rem] text-ink-500">
          Ajoute les 15 chats du bar d'un coup, ou cree-les un par un.
        </p>
        <Button variant="primary" onClick={() => void handleSeed()} loading={seeding}>
          Ajouter les 15 chats du bar
        </Button>
      </div>
    );
  } else if (filteredCats.length === 0) {
    // Des chats existent mais aucun ne passe la recherche ou le filtre de statut.
    content = <p className="px-5 py-6 text-sm text-ink-500">Aucun chat ne correspond.</p>;
  } else {
    content = (
      <ul>
        {filteredCats.map((cat) => {
          const status = adoptionStatusOf(cat.adoptionStatus);
          return (
            <li
              key={cat.id}
              onClick={() => onSelectCat(cat.id)}
              className="flex cursor-pointer items-center gap-3.5 border-b border-border-soft px-5 py-2.5 hover:bg-surface-1"
            >
              <Avatar size={30} initials={cat.name.slice(0, 2)} />
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink-900">{cat.name}</span>
                <span className="block truncate text-xs text-ink-500">
                  {cat.character.length > 0 ? cat.character.join(', ') : 'Sans caractere'}
                </span>
              </div>
              {cat.vaccinesUpToDate && <IconCheck size={16} className="text-accent" />}
              <StatusBadge tone={status.tone} label={status.label} />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ScreenLayout
      appName="Cat Manager Six"
      appIcon={<IconRabbit size={18} />}
      menuItems={menuItems}
      activeMenuId={activeTab}
      onMenuSelect={(id) => onTabChange(id as AppTab)}
      /* Zone 2 : le h1 affiche le label de l'onglet actif ("Les chats") ;
         `title` reste le repli. */
      title="Les chats"
      /* Zone 3 : stats selectionnees dans les reglages (hub Parametres),
         valeurs toujours calculees des donnees reelles. */
      headerStats={headerStats}
      onSearchChange={setQuery}
      searchValue={query}
      searchPlaceholder="Rechercher un chat"
      filters={filters}
      activeFilterId={statusFilter}
      onFilterSelect={(id) => setStatusFilter(id)}
      quickActions={[
        // Contrat v2 : l'action d'ajout se declare `kind: 'add'`, le gabarit
        // impose le libelle universel "Ajouter", IconPlus et le lime (regle G5).
        {
          id: 'add-cat',
          label: 'Ajouter',
          kind: 'add',
          disabled: loadingList || listError != null,
          onClick: () => setShowForm(true),
        },
      ]}
      loading={loadingList && cats.length === 0}
      error={listError}
      errorTitle="Echec du chargement"
      onRetry={() => void refresh()}
      retryLabel="Reessayer"
    >
      {content}

      {/* Formulaire d'ajout : pop-up centrale standard, jamais en zone 5 (regle G6). */}
      <Modal
        open={showForm}
        onClose={closeForm}
        title="Nouveau chat"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeForm}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleCreate()}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cat-name" className="text-xs font-medium text-ink-500">
              Nom
            </label>
            <Input
              id="cat-name"
              type="text"
              className="h-10"
              value={fName}
              placeholder="Ex. Minou"
              onChange={(event) => setFName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Statut</span>
            <div className="flex flex-wrap gap-2">
              {ADOPTION_STATUSES.map((status) => (
                <Button
                  key={status.value}
                  size="sm"
                  variant={status.value === fStatus ? 'primary' : 'secondary'}
                  onClick={() => setFStatus(status.value)}
                >
                  {status.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Caractere</span>
            <div className="flex flex-wrap gap-2">
              {CHARACTER_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={fCharacter.includes(option.value) ? 'primary' : 'secondary'}
                  onClick={() => toggleCharacter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Vaccins</span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={fVaccines ? 'primary' : 'secondary'}
                onClick={() => setFVaccines(true)}
              >
                A jour
              </Button>
              <Button
                size="sm"
                variant={fVaccines ? 'secondary' : 'primary'}
                onClick={() => setFVaccines(false)}
              >
                A verifier
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cat-year" className="text-xs font-medium text-ink-500">
                Annee de naissance
              </label>
              <Input
                id="cat-year"
                type="number"
                className="h-10"
                value={fBirthYear}
                placeholder="Ex. 2021"
                onChange={(event) => setFBirthYear(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cat-diet" className="text-xs font-medium text-ink-500">
                Regime alimentaire
              </label>
              <Input
                id="cat-diet"
                type="text"
                className="h-10"
                value={fDiet}
                placeholder="Optionnel"
                onChange={(event) => setFDiet(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="cat-photo" className="text-xs font-medium text-ink-500">
              Lien de la photo
            </label>
            <Input
              id="cat-photo"
              type="text"
              className="h-10"
              value={fPhoto}
              placeholder="Optionnel"
              onChange={(event) => setFPhoto(event.target.value)}
            />
          </div>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>
    </ScreenLayout>
  );
}

export default CatsListScreen;
